/**
 * React Hook for Primitive Formula System
 *
 * Provides access to proven primitive combinations and interaction patterns.
 * Use this to:
 * - Get formula recommendations based on current workflow
 * - Validate primitives for anti-patterns
 * - Display formula library to users
 */

import { useMutation, useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useCallback } from 'react';

// ============================================================================
// TYPES
// ============================================================================

export enum PrimitiveType {
  // PHASE 1: DESIGN
  WORKFLOW_DEFINITION = 'WORKFLOW_DEFINITION',
  WORKFLOW_NODE = 'WORKFLOW_NODE',
  WORKFLOW_EDGE = 'WORKFLOW_EDGE',
  TEMPLATE = 'TEMPLATE',
  DEPENDENCY = 'DEPENDENCY',
  CONTEXT = 'CONTEXT',
  POLICY = 'POLICY',
  ROLE = 'ROLE',

  // PHASE 2: CONFIGURE
  TEAM = 'TEAM',
  ACTOR = 'ACTOR',
  ACTOR_DESCRIPTOR = 'ACTOR_DESCRIPTOR',
  VALIDATION = 'VALIDATION',
  SLA = 'SLA',
  CONSTRAINT = 'CONSTRAINT',

  // PHASE 3: EXECUTE
  INSTANCE = 'INSTANCE',
  TASK = 'TASK',
  APPROVAL = 'APPROVAL',
  DOCUMENT = 'DOCUMENT',
  QUEUE = 'QUEUE',

  // PHASE 4: MONITOR
  EVENT = 'EVENT',
  NOTIFICATION = 'NOTIFICATION',
  TRACE = 'TRACE',
  CHECKPOINT = 'CHECKPOINT',
  TRANSACTION = 'TRANSACTION',

  // PHASE 5: OPTIMIZE
  METRIC = 'METRIC',
  SCHEDULE = 'SCHEDULE',
  RESOURCE = 'RESOURCE',
  CONTRACT = 'CONTRACT',
  INTEGRATION = 'INTEGRATION',
}

export interface Formula {
  id: string;
  name: string;
  description: string;
  primitives: PrimitiveType[];
  interactions: FormulaInteraction[];
  successRate: number;
  usageCount: number;
  useCases: string[];
  discoveryMethod: DiscoveryMethod;
}

export interface FormulaInteraction {
  from: PrimitiveType;
  to: PrimitiveType;
  relationship: InteractionType;
  description: string;
}

export enum InteractionType {
  REQUIRES = 'REQUIRES',
  ENHANCES = 'ENHANCES',
  MONITORS = 'MONITORS',
  TRIGGERS = 'TRIGGERS',
  VALIDATES = 'VALIDATES',
  ASSIGNED_TO = 'ASSIGNED_TO',
}

export enum DiscoveryMethod {
  MANUAL = 'MANUAL',
  USAGE_ANALYSIS = 'USAGE_ANALYSIS',
  MACHINE_LEARNING = 'MACHINE_LEARNING',
  SYSTEM_RECOMMENDED = 'SYSTEM_RECOMMENDED',
}

export interface FormulaRecommendation {
  formula: Formula;
  missingPrimitives: PrimitiveType[];
  matchPercentage: number;
  confidenceScore: number;
}

export interface ValidationWarning {
  severity: WarningSeverity;
  message: string;
  suggestion: string;
}

export enum WarningSeverity {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
}

// ============================================================================
// GRAPHQL QUERIES
// ============================================================================

const GET_ALL_FORMULAS = gql`
  query GetAllFormulas {
    formulas {
      id
      name
      description
      primitives
      interactions {
        from
        to
        relationship
        description
      }
      successRate
      usageCount
      useCases
      discoveryMethod
    }
  }
`;

const GET_FORMULA = gql`
  query GetFormula($id: ID!) {
    formula(id: $id) {
      id
      name
      description
      primitives
      interactions {
        from
        to
        relationship
        description
      }
      successRate
      usageCount
      useCases
      discoveryMethod
    }
  }
`;

const GET_FORMULAS_BY_PRIMITIVE = gql`
  query GetFormulasByPrimitive($primitive: PrimitiveType!) {
    formulasByPrimitive(primitive: $primitive) {
      id
      name
      description
      primitives
      successRate
      usageCount
      useCases
    }
  }
`;

const RECOMMEND_FORMULAS = gql`
  query RecommendFormulas($currentPrimitives: [PrimitiveType!]!) {
    recommendFormulas(currentPrimitives: $currentPrimitives) {
      formula {
        id
        name
        description
        primitives
        successRate
        usageCount
        useCases
      }
      missingPrimitives
      matchPercentage
      confidenceScore
    }
  }
`;

const VALIDATE_PRIMITIVES = gql`
  query ValidatePrimitives($primitives: [PrimitiveType!]!) {
    validatePrimitives(primitives: $primitives) {
      severity
      message
      suggestion
    }
  }
`;

const REGISTER_FORMULA = gql`
  mutation RegisterFormula($input: FormulaInput!) {
    registerFormula(input: $input) {
      id
      name
      description
      successRate
      usageCount
    }
  }
`;

// ============================================================================
// HOOKS
// ============================================================================

/**
 * Get all formulas
 */
export function useFormulas() {
  const { data, loading, error, refetch } = useQuery<Record<string, any>>(GET_ALL_FORMULAS);

  return {
    formulas: (data?.formulas || []) as Formula[],
    loading,
    error,
    refetch,
  };
}

/**
 * Get a single formula by ID
 */
export function useFormula(id: string) {
  const { data, loading, error } = useQuery<Record<string, any>>(GET_FORMULA, {
    variables: { id },
    skip: !id,
  });

  return {
    formula: data?.formula as Formula | undefined,
    loading,
    error,
  };
}

/**
 * Find formulas that use a specific primitive
 */
export function useFormulasByPrimitive(primitive: PrimitiveType) {
  const { data, loading, error } = useQuery<Record<string, any>>(GET_FORMULAS_BY_PRIMITIVE, {
    variables: { primitive },
    skip: !primitive,
  });

  return {
    formulas: (data?.formulasByPrimitive || []) as Formula[],
    loading,
    error,
  };
}

/**
 * Get formula recommendations based on current workflow primitives
 *
 * @example
 * ```tsx
 * const { recommendations } = useFormulaRecommendations([
 *   PrimitiveType.TASK,
 *   PrimitiveType.ACTOR,
 * ]);
 *
 * // Shows: "Add SLA + Notification for Time-Bound Assignment (94% success)"
 * ```
 */
export function useFormulaRecommendations(currentPrimitives: PrimitiveType[]) {
  const { data, loading, error } = useQuery<Record<string, any>>(RECOMMEND_FORMULAS, {
    variables: { currentPrimitives },
    skip: !currentPrimitives || currentPrimitives.length === 0,
  });

  const recommendations = (data?.recommendFormulas || []) as FormulaRecommendation[];

  // Sort by confidence score (highest first)
  const sortedRecommendations = [...recommendations].sort(
    (a, b) => b.confidenceScore - a.confidenceScore,
  );

  return {
    recommendations: sortedRecommendations,
    topRecommendation: sortedRecommendations[0],
    loading,
    error,
  };
}

/**
 * Validate primitives for anti-patterns
 *
 * @example
 * ```tsx
 * const { warnings, hasWarnings } = usePrimitiveValidation([
 *   PrimitiveType.SLA,
 *   PrimitiveType.TASK,
 * ]);
 *
 * if (hasWarnings) {
 *   console.warn('Anti-pattern detected:', warnings[0].message);
 * }
 * ```
 */
export function usePrimitiveValidation(primitives: PrimitiveType[]) {
  const { data, loading, error } = useQuery<Record<string, any>>(VALIDATE_PRIMITIVES, {
    variables: { primitives },
    skip: !primitives || primitives.length === 0,
  });

  const warnings = (data?.validatePrimitives || []) as ValidationWarning[];

  // Group by severity
  const highWarnings = warnings.filter((w) => w.severity === WarningSeverity.HIGH);
  const mediumWarnings = warnings.filter((w) => w.severity === WarningSeverity.MEDIUM);
  const lowWarnings = warnings.filter((w) => w.severity === WarningSeverity.LOW);

  return {
    warnings,
    hasWarnings: warnings.length > 0,
    highWarnings,
    mediumWarnings,
    lowWarnings,
    loading,
    error,
  };
}

/**
 * Register a new formula (for ML-discovered patterns)
 */
export function useRegisterFormula() {
  const [registerMutation, { data, loading, error }] = useMutation<Record<string, any>>(REGISTER_FORMULA);

  const register = useCallback(
    async (formula: Omit<Formula, 'id'> & { id?: string }) => {
      const result = await registerMutation({
        variables: {
          input: {
            id: formula.id || `formula-${Date.now()}`,
            name: formula.name,
            description: formula.description,
            primitives: formula.primitives,
            interactions: formula.interactions,
            successRate: formula.successRate,
            usageCount: formula.usageCount,
            useCases: formula.useCases,
            discoveryMethod: formula.discoveryMethod,
          },
        },
      });

      return result.data?.registerFormula as Formula;
    },
    [registerMutation],
  );

  return {
    register,
    loading,
    error,
  };
}

// ============================================================================
// UTILITY HOOKS
// ============================================================================

/**
 * Get the "chemical reaction" - what happens when you combine primitives
 *
 * @example
 * ```tsx
 * const { reaction, isProven } = usePrimitiveReaction([
 *   PrimitiveType.TASK,
 *   PrimitiveType.ACTOR,
 *   PrimitiveType.SLA,
 *   PrimitiveType.NOTIFICATION,
 * ]);
 *
 * console.log(reaction); // "Time-Bound Assignment (94% success)"
 * ```
 */
export function usePrimitiveReaction(primitives: PrimitiveType[]) {
  const { formulas } = useFormulas();

  // Find exact match
  const exactMatch = formulas.find((formula) => {
    if (formula.primitives.length !== primitives.length) return false;
    return formula.primitives.every((p) => primitives.includes(p));
  });

  // Find partial matches
  const partialMatches = formulas.filter((formula) => {
    const matchCount = formula.primitives.filter((p) => primitives.includes(p)).length;
    return matchCount >= 2 && matchCount < formula.primitives.length;
  });

  return {
    reaction: exactMatch?.name,
    isProven: !!exactMatch,
    successRate: exactMatch?.successRate,
    partialMatches,
  };
}

/**
 * Smart workflow builder hook - combines recommendations and validation
 *
 * @example
 * ```tsx
 * function WorkflowBuilder() {
 *   const [primitives, setPrimitives] = useState<PrimitiveType[]>([]);
 *   const builder = useSmartWorkflowBuilder(primitives);
 *
 *   return (
 *     <div>
 *       {builder.suggestions.map(s => (
 *         <Suggestion key={s.formula.id} {...s} />
 *       ))}
 *       {builder.warnings.map(w => (
 *         <Warning key={w.message} {...w} />
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export function useSmartWorkflowBuilder(currentPrimitives: PrimitiveType[]) {
  const { recommendations, loading: loadingRecs } = useFormulaRecommendations(currentPrimitives);
  const { warnings, loading: loadingWarnings } = usePrimitiveValidation(currentPrimitives);
  const { reaction, isProven } = usePrimitiveReaction(currentPrimitives);

  return {
    // What to add
    suggestions: recommendations,
    topSuggestion: recommendations[0],

    // What to fix
    warnings,
    hasIssues: warnings.length > 0,

    // What you built
    currentFormula: reaction,
    isProvenPattern: isProven,

    // Loading state
    loading: loadingRecs || loadingWarnings,
  };
}
